import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { PageShell } from '../../components/Ui';
import { useAuth } from '../../auth/AuthContext';
import { ipApi } from '../../services/api';
import { useLiveData } from '../../hooks/useLiveData';
import { useHospitalProfile } from '../../hooks/useHospitalProfile';
import { useHmsConfig } from '../../hooks/useHmsRecords';

/* ═══════════════════════════════════════════════════════════
   SMS / WHATSAPP NOTIFICATION CENTER
   - Template library + custom templates (persisted)
   - Token substitution: [NAME] [DATE] [TIME] [UHID] [HOSPITAL]
   - Single + bulk send via hospital SMS gateway (MSG91/Twilio)
   - Patient multi-select for bulk
   - Delivery log
   - WhatsApp deep-link
═══════════════════════════════════════════════════════════ */

const DEFAULT_TEMPLATES = [
  { id: 'appt_reminder', category: 'Appointment', label: 'Appointment Reminder', body: (h, p) => `Dear ${p || 'Patient'}, your appointment at ${h} is confirmed for [DATE] at [TIME]. Please arrive 10 minutes early with your UHID card. For rescheduling call reception. — ${h}` },
  { id: 'appt_confirm', category: 'Appointment', label: 'Appointment Confirmed', body: (h, p) => `✅ Appointment Confirmed! Dear ${p || 'Patient'}, your appointment with [DOCTOR] at ${h} on [DATE] [TIME] is confirmed. Token: #[TOKEN]. — ${h}` },
  { id: 'receipt', category: 'Billing', label: 'Payment Receipt', body: (h, p) => `Dear ${p || 'Patient'}, your payment of ₹[AMOUNT] at ${h} has been received. Receipt No: [RECEIPT#]. Thank you for choosing us. — ${h}` },
  { id: 'advance', category: 'Billing', label: 'Advance Payment', body: (h, p) => `Dear ${p || 'Patient'}, advance payment of ₹[AMOUNT] against your admission at ${h} has been recorded. Balance payable at discharge: ₹[BALANCE]. — ${h}` },
  { id: 'discharge_bill', category: 'Billing', label: 'Discharge Bill Ready', body: (h, p) => `Dear ${p || 'Patient'}, your discharge bill of ₹[AMOUNT] at ${h} is ready. Please visit billing counter for clearance. — ${h}` },
  { id: 'lab_ready', category: 'Lab', label: 'Lab Report Ready', body: (h, p) => `Dear ${p || 'Patient'}, your lab report from ${h} is ready for collection. Visit the lab desk with your UHID. — ${h}` },
  { id: 'critical_lab', category: 'Lab', label: 'Critical Lab Value Alert', body: (h, p) => `⚠️ ALERT: Critical lab value detected for ${p || 'patient'}. Please contact ${h} immediately at [PHONE]. — ${h} Lab` },
  { id: 'radiology_ready', category: 'Radiology', label: 'Radiology Report Ready', body: (h, p) => `Dear ${p || 'Patient'}, your radiology report ([STUDY]) from ${h} is ready. Collect from radiology counter with UHID. — ${h}` },
  { id: 'followup', category: 'Follow-up', label: 'Follow-up Reminder', body: (h, p) => `Dear ${p || 'Patient'}, this is a reminder for your follow-up visit at ${h} scheduled on [DATE]. Do not skip your follow-up for better health outcomes. — ${h}` },
  { id: 'discharge', category: 'IPD', label: 'Discharge Instructions', body: (h, p) => `Dear ${p || 'Patient'}, you have been discharged from ${h}. Please follow all discharge instructions. Your next visit: [DATE]. For emergency call [PHONE]. — ${h}` },
  { id: 'admit', category: 'IPD', label: 'Admission Confirmation', body: (h, p) => `Dear ${p || 'Patient'}, your admission at ${h} is confirmed. Ward: [WARD], Bed: [BED], Doctor: [DOCTOR]. Family contact: [PHONE]. — ${h}` },
  { id: 'pharmacy_ready', category: 'Pharmacy', label: 'Medicine Ready', body: (h, p) => `Dear ${p || 'Patient'}, your medicines prescribed by [DOCTOR] are ready at the pharmacy counter of ${h}. — ${h}` },
  { id: 'feedback', category: 'Feedback', label: 'Feedback Request', body: (h, p) => `Dear ${p || 'Patient'}, thank you for visiting ${h}. Please share your feedback at [LINK] to help us improve our services. Your feedback matters! — ${h}` },
  { id: 'ot_schedule', category: 'OT', label: 'Surgery Scheduled', body: (h, p) => `Dear ${p || 'Patient'}, your surgery at ${h} is scheduled for [DATE] at [TIME]. OT: [OT]. Surgeon: [DOCTOR]. Please follow pre-operative instructions. — ${h}` },
  { id: 'emergency', category: 'Emergency', label: 'Emergency Notification', body: (h) => `🚨 EMERGENCY: Patient [NAME] has been admitted to ${h} Emergency. Please contact ${h} at [PHONE] immediately for updates. — ${h}` },
];

const CATEGORIES = ['All', ...Array.from(new Set(DEFAULT_TEMPLATES.map(t => t.category)))];

function substituteTokens(message, ctx) {
  return message
    .replace(/\[NAME\]/g, ctx.name || '')
    .replace(/\[DATE\]/g, ctx.date || '')
    .replace(/\[TIME\]/g, ctx.time || '')
    .replace(/\[UHID\]/g, ctx.uhid || '')
    .replace(/\[HOSPITAL\]/g, ctx.hospital || '');
}

export default function SmsCenter() {
  const { user } = useAuth();
  const { hospital } = useHospitalProfile();
  const hospitalName = hospital?.name || user?.hospital || 'Your Hospital';
  const smsCredits = hospital?.smsCredits;
  const gatewayReady = hospital?.smsGatewayEnabled && hospital?.smsProvider && hospital?.smsApiKeySet;
  const smsUnitPrice = Number(hospital?.smsPricePerUnit ?? 0.25);

  const { data, loading, error, reload } = useLiveData(() => ipApi.getSmsLog(), []);
  const { config: tplConfig, save: saveTplConfig } = useHmsConfig('sms-templates');
  const sent = (data?.logs || []).map((l) => ({
    id: l.logId || l.id,
    phone: l.phone,
    patient: l.patientName || l.patient || 'Unknown',
    template: l.template,
    message: l.message,
    status: l.status || 'Logged',
    sentAt: l.sentAt,
  }));

  const [templates, setTemplates] = useState(DEFAULT_TEMPLATES);
  const [tab, setTab] = useState('compose');
  const [selectedTpl, setSelectedTpl] = useState(templates[0].id);
  const [phone, setPhone] = useState('');
  const [patientName, setPatientName] = useState('');
  const [customMsg, setCustomMsg] = useState('');
  const [category, setCategory] = useState('All');
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState('');
  const [copied, setCopied] = useState('');
  const [bulkNumbers, setBulkNumbers] = useState('');
  const [newTpl, setNewTpl] = useState({ label: '', category: 'Custom', body: '' });
  const [showBuilder, setShowBuilder] = useState(false);

  const [tokenCtx, setTokenCtx] = useState({
    name: '',
    date: new Date().toLocaleDateString('en-IN'),
    time: new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }),
    uhid: '',
    hospital: hospitalName,
  });

  const [patientSearchQ, setPatientSearchQ] = useState('');
  const [patientSearchResults, setPatientSearchResults] = useState([]);
  const [selectedBulkPatients, setSelectedBulkPatients] = useState([]);
  const [patientSearchBusy, setPatientSearchBusy] = useState(false);

  useEffect(() => {
    setTokenCtx(c => ({ ...c, hospital: hospitalName }));
  }, [hospitalName]);

  useEffect(() => {
    const custom = Array.isArray(tplConfig?.customTemplates) ? tplConfig.customTemplates : [];
    if (!custom.length) return;
    setTemplates([
      ...DEFAULT_TEMPLATES,
      ...custom.map((t) => ({
        ...t,
        body: typeof t.body === 'string' ? () => t.body : t.body,
      })),
    ]);
  }, [tplConfig]);

  const searchPatients = async (q) => {
    if (!q || q.length < 2) { setPatientSearchResults([]); return; }
    setPatientSearchBusy(true);
    try {
      const res = await ipApi.getPatients({ search: q, limit: 40 });
      setPatientSearchResults(res?.patients || []);
    } catch { setPatientSearchResults([]); } finally { setPatientSearchBusy(false); }
  };

  const toggleBulkPatient = (p) => {
    setSelectedBulkPatients(prev => {
      const id = p.patientId || p.uhid;
      const exists = prev.find(x => (x.patientId || x.uhid) === id);
      if (exists) return prev.filter(x => (x.patientId || x.uhid) !== id);
      return [...prev, p];
    });
  };

  const applyBulkPatients = () => {
    const numbers = selectedBulkPatients.map(p => p.mobile || p.phone || '').filter(Boolean);
    setBulkNumbers(numbers.join('\n'));
    setPatientSearchResults([]);
    setPatientSearchQ('');
  };

  const filteredTemplates = category === 'All' ? templates : templates.filter(t => t.category === category);
  const tpl = templates.find(t => t.id === selectedTpl) || templates[0];
  const rawMessage = customMsg || (typeof tpl.body === 'function' ? tpl.body(hospitalName, patientName) : tpl.body);
  const message = substituteTokens(rawMessage, { ...tokenCtx, name: tokenCtx.name || patientName });

  const whatsappUrl = phone ? `https://wa.me/${phone.replace(/\D/g, '')}?text=${encodeURIComponent(message)}` : null;

  async function sendSms() {
    if (!phone.trim()) { setMsg('Enter a mobile number.'); return; }
    setSending(true);
    setMsg('');
    try {
      const res = await ipApi.sendSms({
        phone: phone.trim(),
        patientName,
        template: tpl.label,
        message,
        channel: 'SMS',
      });
      const entry = res?.logs?.[0];
      const status = entry?.status || 'Logged';
      if (status === 'Sent') setMsg('SMS sent via gateway.');
      else if (status === 'Failed') setMsg(`SMS failed: ${entry?.providerRef || 'gateway error'}. Check MSG91 key in hospital profile.`);
      else setMsg(res?.live ? 'SMS logged.' : 'SMS logged (gateway not configured — add MSG91 auth key for live delivery).');
      await reload();
      setPhone('');
      setPatientName('');
      setCustomMsg('');
    } catch (err) {
      setMsg(err.message || 'Failed to send SMS');
    } finally {
      setSending(false);
    }
  }

  async function sendBulk() {
    const numbers = bulkNumbers.split('\n').map((n) => n.trim()).filter(Boolean);
    if (!numbers.length) { setMsg('Enter at least one phone number.'); return; }
    setSending(true);
    setMsg('');
    try {
      const res = await ipApi.sendSms({
        phones: numbers,
        template: tpl.label,
        message,
        channel: 'SMS',
      });
      const sentOk = (res?.logs || []).filter((l) => l.status === 'Sent').length;
      const failed = (res?.logs || []).filter((l) => l.status === 'Failed').length;
      setMsg(res?.live
        ? `Bulk done: ${sentOk} sent, ${failed} failed, ${(res?.logs || []).length} total.`
        : `${(res?.logs || []).length} messages logged (gateway not configured — add MSG91 for live SMS).`);
      await reload();
      setBulkNumbers('');
      setSelectedBulkPatients([]);
    } catch (err) {
      setMsg(err.message || 'Failed to send bulk SMS');
    } finally {
      setSending(false);
    }
  }

  async function addTemplate() {
    if (!newTpl.label || !newTpl.body) return;
    const row = { ...newTpl, id: 'custom_' + Date.now(), body: newTpl.body };
    const nextCustom = [
      ...(Array.isArray(tplConfig?.customTemplates) ? tplConfig.customTemplates : []),
      row,
    ];
    try {
      await saveTplConfig({ customTemplates: nextCustom });
      setTemplates((t) => [...t, { ...row, body: () => row.body }]);
      setNewTpl({ label: '', category: 'Custom', body: '' });
      setShowBuilder(false);
      setMsg('Template saved.');
    } catch (err) {
      setMsg(err.message || 'Could not save template');
    }
  }

  async function copyMessage() {
    try { await navigator.clipboard.writeText(message); setCopied('✓ Copied!'); setTimeout(() => setCopied(''), 2000); } catch { setCopied('Failed'); }
  }

  const statColors = { Sent: '#10b981', Logged: '#10b981', Queued: '#f59e0b', Failed: '#dc2626' };
  const todayCount = sent.filter((s) => (s.sentAt || '').slice(0, 10) === new Date().toISOString().slice(0, 10)).length;

  return (
    <PageShell
      title="SMS & WhatsApp Center"
      subtitle="Message templates and delivery log — connect SMS gateway in hospital settings for live delivery"
    >
      {error && <p className="hint" style={{ color: '#dc2626', marginBottom: 10 }}>{error} <button type="button" className="btn btn-sm btn-secondary" onClick={reload}>Retry</button></p>}
      {msg && <div className={`mb ${/fail|error|could|not configured/i.test(msg) && !/sent/i.test(msg) ? 'error' : 'info-banner'}`}>{msg}</div>}
      <div style={{ padding: '10px 14px', background: '#f0f9ff', border: '1.5px solid #bae6fd', borderRadius: 10, marginBottom: 12, fontSize: 13, color: '#0c4a6e' }}>
        ℹ Auto SMS fires automatically when: <strong>lab report verified</strong>, <strong>radiology report verified</strong>, and <strong>IP admit</strong> — provided the patient has a phone number and MSG91 credits are available.
      </div>
      <div className={`info-banner mb ${gatewayReady ? '' : ''}`} style={gatewayReady ? { background: '#f0fdf4', borderColor: '#bbf7d0', color: '#166534' } : undefined}>
        {gatewayReady
          ? `SMS gateway configured — ${String(hospital.smsProvider || 'MSG91').toUpperCase()}. Click Send to deliver via gateway.`
          : <>Add MSG91 auth key on hospital profile for live SMS. Until then, Send logs the message and WhatsApp deep-link works instantly.</>}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(130px,1fr))', gap: 10, marginBottom: 16 }}>
        {[{ label: 'SMS Credits', val: gatewayReady ? (smsCredits ?? '—') : 'Not set', color: '#0d6e6a' }, { label: 'Templates', val: templates.length, color: '#6366f1' }, { label: 'Sent Today', val: todayCount, color: '#10b981' }, { label: 'Queued', val: sent.filter(s => s.status === 'Queued').length, color: '#f59e0b' }].map(s => (
          <div key={s.label} className="card" style={{ padding: 12, textAlign: 'center', borderTop: `3px solid ${s.color}` }}>
            <div style={{ fontFamily: 'Outfit,sans-serif', fontSize: 22, fontWeight: 900, color: s.color }}>{s.val}</div>
            <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>

      <div className="period-tabs card mb" style={{ flexWrap: 'wrap' }}>
        {[{ id: 'compose', label: '✍ Compose & Send' }, { id: 'bulk', label: '📢 Bulk Campaign' }, { id: 'templates', label: '📋 Template Library' }, { id: 'log', label: `📊 Delivery Log (${sent.length})` }].map(({ id, label }) => (
          <button key={id} type="button" className={`emr-chip ${tab === id ? 'active' : ''}`} onClick={() => setTab(id)}>{label}</button>
        ))}
      </div>

      {tab === 'compose' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <div className="card" style={{ borderLeft: '4px solid #10b981' }}>
            <h3 style={{ marginTop: 0 }}>📱 Compose Message</h3>
            <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
              {CATEGORIES.map(c => <button key={c} type="button" className={`emr-chip ${category === c ? 'active' : ''}`} style={{ fontSize: 11 }} onClick={() => setCategory(c)}>{c}</button>)}
            </div>
            <div className="field mb">
              <label>Template</label>
              <select className="input" value={selectedTpl} onChange={e => { setSelectedTpl(e.target.value); setCustomMsg(''); }}>
                {filteredTemplates.map(t => <option key={t.id} value={t.id}>{t.category}: {t.label}</option>)}
              </select>
            </div>
            <div style={{ padding: '10px 12px', background: '#f8fafc', borderRadius: 8, marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#475569', marginBottom: 8 }}>Token Context (fill to substitute [NAME] [DATE] [TIME] [UHID] [HOSPITAL])</div>
              <div className="form-grid" style={{ gap: 6 }}>
                <div className="field" style={{ margin: 0 }}><label style={{ fontSize: 11 }}>NAME</label><input className="input" value={tokenCtx.name} onChange={e => setTokenCtx(c => ({ ...c, name: e.target.value }))} placeholder="Patient name" /></div>
                <div className="field" style={{ margin: 0 }}><label style={{ fontSize: 11 }}>DATE</label><input className="input" value={tokenCtx.date} onChange={e => setTokenCtx(c => ({ ...c, date: e.target.value }))} /></div>
                <div className="field" style={{ margin: 0 }}><label style={{ fontSize: 11 }}>TIME</label><input className="input" value={tokenCtx.time} onChange={e => setTokenCtx(c => ({ ...c, time: e.target.value }))} /></div>
                <div className="field" style={{ margin: 0 }}><label style={{ fontSize: 11 }}>UHID</label><input className="input" value={tokenCtx.uhid} onChange={e => setTokenCtx(c => ({ ...c, uhid: e.target.value }))} /></div>
              </div>
            </div>
            <div className="field mb">
              <label>Patient Name (for personalisation)</label>
              <input className="input" value={patientName} onChange={e => setPatientName(e.target.value)} placeholder="e.g. Rajesh Kumar" />
            </div>
            <div className="field mb">
              <label>Patient Mobile *</label>
              <input className="input" value={phone} onChange={e => setPhone(e.target.value)} placeholder="91XXXXXXXXXX" />
            </div>
            <div className="field mb">
              <label>Message (editable)</label>
              <textarea className="input" rows={5} value={customMsg || rawMessage} onChange={e => setCustomMsg(e.target.value)} />
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button type="button" className="btn btn-secondary btn-sm" onClick={copyMessage}>{copied || '📋 Copy'}</button>
              {whatsappUrl && <a className="btn btn-primary btn-sm" href={whatsappUrl} target="_blank" rel="noreferrer">💬 WhatsApp</a>}
              <button type="button" className="btn btn-primary btn-sm" onClick={sendSms} disabled={!phone || sending || !gatewayReady} title={!gatewayReady ? 'Configure SMS gateway in Settings' : undefined}>{sending ? 'Sending…' : gatewayReady ? '📤 Send SMS' : 'Gateway required'}</button>
            </div>
          </div>
          <div className="card">
            <h3 style={{ marginTop: 0 }}>📋 Message Preview</h3>
            <div style={{ background: '#075e54', borderRadius: 16, padding: 20, color: '#fff', minHeight: 180 }}>
              <div style={{ background: '#dcf8c6', color: '#1e293b', borderRadius: '0 12px 12px 12px', padding: '12px 16px', fontSize: 14, lineHeight: 1.7, maxWidth: '90%', boxShadow: '0 2px 4px rgba(0,0,0,0.15)' }}>
                {message}
              </div>
              <div style={{ fontSize: 11, color: '#a7f3d0', marginTop: 8, textAlign: 'right' }}>Sent ✓✓</div>
            </div>
            <div style={{ marginTop: 14, padding: '10px 12px', background: '#f8fafc', borderRadius: 8, fontSize: 12, color: '#64748b' }}>
              📏 Length: <strong>{message.length}</strong> chars · {Math.ceil(message.length / 160)} SMS unit(s) · Approx cost: ₹{(Math.ceil(message.length / 160) * smsUnitPrice).toFixed(2)}/msg
            </div>
            <div className="card mt" style={{ marginTop: 12 }}>
              <h4 style={{ marginTop: 0 }}>Quick Links</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {[{ to: '/hms/appointments', label: '📅 Appointments Queue' }, { to: '/billing/receipts', label: '💰 Billing & Receipts' }, { to: '/lab/orders', label: '🧪 Lab Orders' }, { to: '/reception/patients', label: '👤 Patient Registry' }].map(l => (
                  <Link key={l.to} to={l.to} style={{ padding: '8px 12px', background: '#f1f5f9', borderRadius: 8, fontSize: 13, fontWeight: 600, color: '#0d6e6a', textDecoration: 'none' }}>{l.label}</Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === 'bulk' && (
        <div className="card" style={{ borderLeft: '4px solid #f97316' }}>
          <h3 style={{ marginTop: 0, color: '#ea580c' }}>📢 Bulk SMS Campaign</h3>
          <div style={{ padding: '10px 14px', background: '#fff7ed', borderRadius: 8, marginBottom: 14, fontSize: 12, color: '#92400e' }}>
            Enter one mobile number per line or search and select patients below. Message will be sent to all numbers using the selected template.
          </div>
          <div className="field mb">
            <label>Select Template</label>
            <select className="input" value={selectedTpl} onChange={e => setSelectedTpl(e.target.value)}>
              {templates.map(t => <option key={t.id} value={t.id}>{t.category}: {t.label}</option>)}
            </select>
          </div>
          <div className="card mb" style={{ padding: 12 }}>
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>Search & select patients (limit 40)</div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <input className="input" style={{ flex: 1 }} value={patientSearchQ} onChange={e => { setPatientSearchQ(e.target.value); searchPatients(e.target.value); }} placeholder="Name or UHID…" />
              {selectedBulkPatients.length > 0 && <button type="button" className="btn btn-primary btn-sm" onClick={applyBulkPatients}>Add {selectedBulkPatients.length} to list</button>}
            </div>
            {patientSearchBusy && <div style={{ fontSize: 11, color: '#94a3b8' }}>Searching…</div>}
            {patientSearchResults.length > 0 && (
              <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 8 }}>
                {patientSearchResults.map(p => {
                  const id = p.patientId || p.uhid;
                  const selected = !!selectedBulkPatients.find(x => (x.patientId || x.uhid) === id);
                  return (
                    <label key={id} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '7px 10px', borderBottom: '1px solid #f1f5f9', cursor: 'pointer', background: selected ? '#f0fdf4' : '#fff' }}>
                      <input type="checkbox" checked={selected} onChange={() => toggleBulkPatient(p)} />
                      <div style={{ flex: 1, fontSize: 12 }}>
                        <strong>{p.name || p.patientName}</strong>
                        <span style={{ color: '#94a3b8', marginLeft: 6 }}>{p.uhid}</span>
                        {(p.mobile || p.phone) && <span style={{ color: '#64748b', marginLeft: 6 }}>{p.mobile || p.phone}</span>}
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
            {selectedBulkPatients.length > 0 && (
              <div style={{ marginTop: 8, fontSize: 12, color: '#475569' }}>
                Selected: {selectedBulkPatients.map(p => p.name || p.patientName).join(', ')}
              </div>
            )}
          </div>
          <div className="field mb">
            <label>Phone Numbers (one per line)</label>
            <textarea className="input" rows={8} value={bulkNumbers} onChange={e => setBulkNumbers(e.target.value)} placeholder={'919876543210\n919876543211\n919876543212'} />
          </div>
          <div style={{ marginBottom: 14, fontSize: 13, color: '#475569' }}>
            📊 <strong>{bulkNumbers.split('\n').filter(Boolean).length}</strong> numbers · Estimated cost: ₹{(bulkNumbers.split('\n').filter(Boolean).length * smsUnitPrice).toFixed(2)}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="btn btn-primary" onClick={sendBulk} disabled={!bulkNumbers.trim() || sending || !gatewayReady}>{sending ? 'Saving…' : gatewayReady ? '📤 Send Bulk SMS' : 'Gateway required'}</button>
            <button type="button" className="btn btn-secondary" onClick={() => { setBulkNumbers(''); setSelectedBulkPatients([]); }}>Clear</button>
          </div>
        </div>
      )}

      {tab === 'templates' && (
        <div>
          <div style={{ marginBottom: 12 }}>
            <button type="button" className="btn btn-primary" onClick={() => setShowBuilder(s => !s)}>{showBuilder ? '✕ Cancel' : '+ Create Custom Template'}</button>
          </div>
          {showBuilder && (
            <div className="card mb form-grid" style={{ borderLeft: '4px solid #8b5cf6' }}>
              <div className="field"><label>Template Name *</label><input className="input" value={newTpl.label} onChange={e => setNewTpl(n => ({ ...n, label: e.target.value }))} /></div>
              <div className="field"><label>Category</label><input className="input" value={newTpl.category} onChange={e => setNewTpl(n => ({ ...n, category: e.target.value }))} /></div>
              <div className="field full"><label>Message Body *</label><textarea className="input" rows={4} value={newTpl.body} onChange={e => setNewTpl(n => ({ ...n, body: e.target.value }))} placeholder="Use [NAME], [DATE], [TIME], [UHID], [HOSPITAL], [DOCTOR], [AMOUNT] as placeholders..." /></div>
              <div className="full form-actions"><button type="button" className="btn btn-primary" onClick={addTemplate}>Save Template</button></div>
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(300px,1fr))', gap: 10 }}>
            {templates.map(t => (
              <div key={t.id} className="card" style={{ borderLeft: '3px solid #6366f1', cursor: 'pointer' }} onClick={() => { setSelectedTpl(t.id); setCustomMsg(''); setTab('compose'); }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <div style={{ fontWeight: 800, fontSize: 13 }}>{t.label}</div>
                  <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe' }}>{t.category}</span>
                </div>
                <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.5 }}>{typeof t.body === 'function' ? String(t.body(hospitalName, '[Patient]')).slice(0, 100) + '...' : String(t.body || '').slice(0, 100) + '...'}</div>
                <div style={{ marginTop: 8 }}>
                  <button type="button" className="btn btn-primary btn-sm" onClick={e => { e.stopPropagation(); setSelectedTpl(t.id); setCustomMsg(''); setTab('compose'); }}>Use Template →</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'log' && (
        <div className="card">
          <h3 style={{ marginTop: 0 }}>📊 Delivery Log</h3>
          {loading && sent.length === 0 ? <p className="hint">Loading log…</p> : sent.length === 0 ? <p className="hint table-empty">No messages sent yet. Use Compose to send.</p> : (
            <div className="table-wrap">
              <table className="data-table">
                <thead><tr><th>Time</th><th>Phone</th><th>Patient</th><th>Template</th><th>Status</th></tr></thead>
                <tbody>
                  {sent.map(s => (
                    <tr key={s.id}>
                      <td style={{ fontSize: 11 }}>{new Date(s.sentAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</td>
                      <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{s.phone}</td>
                      <td>{s.patient}</td>
                      <td style={{ fontSize: 12, color: '#64748b' }}>{s.template}</td>
                      <td><span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: (statColors[s.status] || '#94a3b8') + '18', color: statColors[s.status] || '#94a3b8', border: `1px solid ${(statColors[s.status] || '#94a3b8')}40` }}>{s.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </PageShell>
  );
}
